import { GeoidModel } from '../../../../typings/api/skymap/rest/v1/.common';

/**
 * Returns the longitude range of a geoid, moved by whole turns to lie around the given longitude.
 *
 * Some geoid grids, such as NOAA's GEOID18 (us_noaa_g2018u0.tif), store longitude as 0..360 and
 * the header keeps that, so the US is 230..300 instead of -130..-60. Moving the range next to the
 * longitude it is compared with also keeps a grid that crosses the antimeridian in one piece.
 */
export function getGeoidLongitudeRange(
  header: NonNullable<GeoidModel['header']>,
  aroundLongitude: number,
): { min: number; max: number } {
  const { min, max } = header.lng;
  const turns = Math.round((aroundLongitude - (min + max) / 2) / 360);

  return { min: min + turns * 360, max: max + turns * 360 };
}

/**
 * Returns the part of the longitude range min..max that a geoid covers.
 *
 * Every copy of the geoid's range, moved by whole turns, that overlaps min..max counts, so the
 * result spans all of them. For a near-global geoid whose gap falls inside min..max the result
 * includes the gap, since a single range can't leave it out. Without any overlap it clamps to the
 * geoid's range as stored.
 */
export function clampLongitudeRangeToGeoid(
  header: NonNullable<GeoidModel['header']>,
  min: number,
  max: number,
): { min: number; max: number } {
  const geoid = header.lng;
  const firstTurn = Math.ceil((min - geoid.max) / 360);
  const lastTurn = Math.floor((max - geoid.min) / 360);

  if (firstTurn > lastTurn) {
    return { min: Math.max(min, geoid.min), max: Math.min(max, geoid.max) };
  }

  return {
    min: Math.max(min, geoid.min + firstTurn * 360),
    max: Math.min(max, geoid.max + lastTurn * 360),
  };
}
